import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { 
  ArrowLeft,
  Loader2,
  Send,
  Copy,
  Edit,
  Trash2,
  ArrowRightCircle,
  FileText,
  Eye,
  CheckCircle,
  XCircle,
  AlertCircle,
  Building2,
  Calendar,
  DollarSign,
  Clock,
  MoreVertical,
  ExternalLink,
  Mail
} from 'lucide-react';
import { format, parseISO } from 'date-fns';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import TenantMobileLayout from './TenantMobileLayout';

const STATUS_CONFIG = {
  draft: { label: 'Draft', color: 'bg-zinc-500', icon: FileText },
  sent: { label: 'Sent', color: 'bg-blue-500', icon: Send },
  viewed: { label: 'Viewed', color: 'bg-purple-500', icon: Eye },
  approved: { label: 'Approved', color: 'bg-green-500', icon: CheckCircle },
  rejected: { label: 'Rejected', color: 'bg-red-500', icon: XCircle },
  expired: { label: 'Expired', color: 'bg-orange-500', icon: AlertCircle },
  converted: { label: 'Converted', color: 'bg-[#ffd108] text-black', icon: ArrowRightCircle }
};

const TenantMobileQuoteDetail = () => {
  const { api } = useAuth();
  const navigate = useNavigate();
  const { id } = useParams();
  
  const [loading, setLoading] = useState(true);
  const [quote, setQuote] = useState(null);
  const [customer, setCustomer] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);

  useEffect(() => {
    fetchQuote();
  }, [id]);

  const fetchQuote = async () => {
    setLoading(true);
    try {
      const response = await api.get(`/quotes/${id}`);
      setQuote(response.data);
      
      if (response.data.customer_id) {
        const customerRes = await api.get(`/customers/${response.data.customer_id}`);
        setCustomer(customerRes.data);
      }
    } catch (error) {
      toast.error('Failed to load quote');
      navigate('/tenant-mobile/quotes');
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    setActionLoading(true);
    try {
      await api.post(`/quotes/${id}/send`);
      toast.success('Quote sent to customer');
      fetchQuote();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send quote');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDuplicate = async () => {
    setActionLoading(true);
    try {
      const response = await api.post(`/quotes/${id}/duplicate`);
      toast.success('Quote duplicated');
      navigate(`/tenant-mobile/quotes/${response.data.id}`);
    } catch (error) {
      toast.error('Failed to duplicate quote');
    } finally {
      setActionLoading(false);
    }
  };

  const handleConvertToJob = async () => {
    setActionLoading(true);
    try {
      const response = await api.post(`/quotes/${id}/convert-to-job`);
      toast.success('Quote converted to job');
      navigate(`/tenant-mobile/jobs/${response.data.job_id}`);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to convert to job');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    setActionLoading(true);
    try {
      await api.delete(`/quotes/${id}`);
      toast.success('Quote deleted');
      navigate('/tenant-mobile/quotes');
    } catch (error) {
      toast.error('Failed to delete quote');
    } finally {
      setActionLoading(false);
      setShowDeleteDialog(false);
    }
  };

  const formatDate = (dateStr) => {
    try {
      return format(parseISO(dateStr), 'dd MMM yyyy');
    } catch {
      return dateStr || '-';
    }
  };

  const getStatusBadge = (status) => {
    const config = STATUS_CONFIG[status] || STATUS_CONFIG.draft;
    const Icon = config.icon;
    return (
      <Badge className={`${config.color} text-white flex items-center gap-1`}>
        <Icon className="h-3 w-3" />
        {config.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <TenantMobileLayout>
        <div className="flex justify-center items-center py-20">
          <Loader2 className="h-8 w-8 animate-spin text-[#ffd108]" />
        </div>
      </TenantMobileLayout>
    );
  }

  if (!quote) return null;

  const canSend = quote.status === 'draft';
  const canEdit = ['draft', 'sent'].includes(quote.status);
  const canConvert = quote.status === 'approved';
  const canDelete = quote.status === 'draft';

  return (
    <TenantMobileLayout>
      {/* Header */}
      <div className="bg-[#2a2a2a] px-4 py-4 sticky top-0 z-20">
        <div className="flex items-center gap-3">
          <Button 
            variant="ghost" 
            size="icon" 
            onClick={() => navigate('/tenant-mobile/quotes')}
            className="text-white"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-white font-bold text-lg">{quote.quote_number}</h1>
            <div className="flex items-center gap-2 mt-1">
              {getStatusBadge(quote.status)}
            </div>
          </div>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" size="icon" className="text-white">
                <MoreVertical className="h-5 w-5" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-[#2a2a2a] border-gray-600">
              {canEdit && (
                <DropdownMenuItem 
                  onClick={() => navigate(`/tenant-mobile/quotes/${id}/edit`)}
                  className="text-white hover:bg-[#3a3a3a]"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Quote
                </DropdownMenuItem>
              )}
              <DropdownMenuItem 
                onClick={handleDuplicate}
                className="text-white hover:bg-[#3a3a3a]"
              >
                <Copy className="h-4 w-4 mr-2" />
                Duplicate
              </DropdownMenuItem>
              {quote.customer_portal_url && (
                <DropdownMenuItem 
                  onClick={() => window.open(quote.customer_portal_url, '_blank')}
                  className="text-white hover:bg-[#3a3a3a]"
                >
                  <ExternalLink className="h-4 w-4 mr-2" />
                  View Customer Link
                </DropdownMenuItem>
              )}
              {canDelete && (
                <>
                  <DropdownMenuSeparator className="bg-gray-600" />
                  <DropdownMenuItem 
                    onClick={() => setShowDeleteDialog(true)}
                    className="text-red-400 hover:bg-red-500/20"
                  >
                    <Trash2 className="h-4 w-4 mr-2" />
                    Delete Quote
                  </DropdownMenuItem>
                </>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      <div className="p-4 space-y-4 pb-32">
        {/* Customer Info Card */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <CardContent className="p-4">
            <div className="flex items-start gap-3">
              <div className="p-2 bg-[#ffd108]/20 rounded-lg">
                <Building2 className="h-5 w-5 text-[#ffd108]" />
              </div>
              <div className="flex-1">
                <h3 className="text-white font-medium">{customer?.company_name || 'Unknown'}</h3>
                {customer?.contact_email && (
                  <p className="text-gray-400 text-sm">{customer.contact_email}</p>
                )}
                {customer?.phone && (
                  <p className="text-gray-400 text-sm">{customer.phone}</p>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quote Details Card */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <CardContent className="p-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-400">
                <Calendar className="h-4 w-4" />
                <span className="text-sm">Created</span>
              </div>
              <span className="text-white text-sm">{formatDate(quote.created_at)}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-400">
                <Clock className="h-4 w-4" />
                <span className="text-sm">Valid Until</span>
              </div>
              <span className="text-white text-sm">{formatDate(quote.valid_until)}</span>
            </div>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2 text-gray-400">
                <FileText className="h-4 w-4" />
                <span className="text-sm">Pricing</span>
              </div>
              <Badge className="bg-[#3a3a3a] text-gray-300 capitalize">{quote.pricing_type}</Badge>
            </div>
            {quote.viewed_at && (
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-gray-400">
                  <Eye className="h-4 w-4" />
                  <span className="text-sm">Viewed</span>
                </div>
                <span className="text-white text-sm">{formatDate(quote.viewed_at)}</span>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Description */}
        {quote.description && (
          <Card className="bg-[#2a2a2a] border-gray-700">
            <CardContent className="p-4">
              <h4 className="text-gray-400 text-sm mb-2">Work Description</h4>
              <p className="text-white text-sm">{quote.description}</p>
            </CardContent>
          </Card>
        )}

        {/* Line Items */}
        <Card className="bg-[#2a2a2a] border-gray-700">
          <CardContent className="p-4">
            <h4 className="text-gray-400 text-sm mb-3">Line Items ({quote.line_items?.length || 0})</h4>
            <div className="space-y-3">
              {quote.line_items?.map((item, index) => (
                <div key={item.id || index} className="bg-[#3a3a3a] rounded-lg p-3">
                  <div className="flex justify-between items-start">
                    <div className="flex-1 min-w-0">
                      <p className="text-white font-medium text-sm truncate">{item.description}</p>
                      <p className="text-gray-400 text-xs mt-1">
                        {item.quantity} × ${item.rate?.toLocaleString()} / {item.unit}
                      </p>
                    </div>
                    <span className="text-[#ffd108] font-medium text-sm ml-3">
                      ${((item.quantity || 0) * (item.rate || 0)).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Photos */}
        {quote.photos && quote.photos.length > 0 && (
          <Card className="bg-[#2a2a2a] border-gray-700">
            <CardContent className="p-4">
              <h4 className="text-gray-400 text-sm mb-3">Reference Photos ({quote.photos.length})</h4>
              <div className="grid grid-cols-3 gap-2">
                {quote.photos.map((photo, index) => (
                  <img
                    key={index}
                    src={photo}
                    alt={`Reference ${index + 1}`}
                    className="w-full aspect-square object-cover rounded-lg"
                  />
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Totals Card */}
        <Card className="bg-[#2a2a2a] border-[#ffd108]/30">
          <CardContent className="p-4">
            <div className="space-y-2">
              <div className="flex justify-between text-gray-400">
                <span>Subtotal</span>
                <span>${quote.subtotal?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="flex justify-between text-gray-400">
                <span>GST ({quote.tax_rate}%)</span>
                <span>${quote.tax_amount?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
              <div className="border-t border-gray-600 pt-2 flex justify-between text-white font-bold text-lg">
                <span>Total</span>
                <span className="text-[#ffd108]">${quote.total?.toLocaleString(undefined, { minimumFractionDigits: 2 })}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Customer Response */}
        {quote.status === 'approved' && quote.customer_signed_at && (
          <Card className="bg-green-500/10 border-green-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <CheckCircle className="h-5 w-5 text-green-500" />
                <span className="text-green-500 font-medium">Approved by Customer</span>
              </div>
              <p className="text-gray-400 text-sm">
                Signed on {formatDate(quote.customer_signed_at)}
              </p>
              {quote.customer_signature_name && (
                <p className="text-gray-400 text-sm">
                  By: {quote.customer_signature_name}
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {quote.status === 'rejected' && (
          <Card className="bg-red-500/10 border-red-500/30">
            <CardContent className="p-4">
              <div className="flex items-center gap-2 mb-2">
                <XCircle className="h-5 w-5 text-red-500" />
                <span className="text-red-500 font-medium">Rejected by Customer</span>
              </div>
              {quote.rejection_reason && (
                <p className="text-gray-400 text-sm">{quote.rejection_reason}</p>
              )}
            </CardContent>
          </Card>
        )}
      </div>

      {/* Fixed Bottom Actions */}
      <div className="fixed bottom-16 left-0 right-0 bg-[#2a2a2a] border-t border-gray-700 p-4 z-10">
        <div className="flex gap-3">
          {canSend && (
            <Button
              onClick={handleSend}
              disabled={actionLoading}
              className="flex-1 bg-[#ffd108] text-[#1a1a1a] hover:bg-[#ffd108]/90"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Send className="h-4 w-4 mr-2" />}
              Send to Customer
            </Button>
          )}
          {canConvert && (
            <Button
              onClick={handleConvertToJob}
              disabled={actionLoading}
              className="flex-1 bg-[#ffd108] text-[#1a1a1a] hover:bg-[#ffd108]/90"
            >
              {actionLoading ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <ArrowRightCircle className="h-4 w-4 mr-2" />}
              Convert to Job
            </Button>
          )}
          {canEdit && (
            <Button
              variant="outline"
              onClick={() => navigate(`/tenant-mobile/quotes/${id}/edit`)}
              className="flex-1 bg-[#3a3a3a] border-gray-600 text-white hover:bg-[#4a4a4a]"
            >
              <Edit className="h-4 w-4 mr-2" />
              Edit
            </Button>
          )}
          {!canSend && !canConvert && !canEdit && (
            <Button
              variant="outline"
              onClick={handleDuplicate}
              disabled={actionLoading}
              className="flex-1 bg-[#3a3a3a] border-gray-600 text-white hover:bg-[#4a4a4a]"
            >
              <Copy className="h-4 w-4 mr-2" />
              Duplicate Quote
            </Button>
          )}
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent className="bg-[#2a2a2a] border-gray-600">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Quote?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This action cannot be undone. This will permanently delete the quote {quote.quote_number}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-[#3a3a3a] border-gray-600 text-white hover:bg-[#4a4a4a]">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-red-500 text-white hover:bg-red-600"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </TenantMobileLayout>
  );
};

export default TenantMobileQuoteDetail;
